import Storages from 'storages';
import Services from 'services';
import httpService from 'services/http/http.service';
import ApiUrl from 'enum/api-url.enum';
import DeviceData from 'drivers/deviceData';
import StorageKeys from 'enum/storages.enum';
import EventBus from 'utils/eventBus';
import Code from 'enum/lang-code.enum';
import Route from 'enum/route.enum';
import { redirectTo } from 'components/common/navigation/redirect';
import WebSocketInstance from 'services/webSocket/webSocket';
import { isLoggedInDevice } from 'utils/isLoggedIn';
import { Landing, Theme } from 'enum/design';
import { applyBrandColors } from 'design-tokens/warwick/tokens';
import { getDeviceGroupId } from 'utils/common/registerDevGroupID';
import Devices from 'drivers/devices';
import environment from 'utils/environment';
import patientData from 'utils/patientData';
import { createChannelDataService } from 'components/channels/common/channelData.service';

// Platform-specific reload helper using existing device abstraction
async function reloadApplication() {
  clearCache([StorageKeys.Keys.ROOMDATA, StorageKeys.Keys.PROXYURL]);
  try {
    // Use the existing DeviceData abstraction which properly delegates to each platform
    await DeviceData.restartScreen();
  } catch (err) {
    console.log('[reloadApplication] Device-specific reload failed, using fallback:', err);
    fallbackReload();
  }
}

function fallbackReload() {
  try {
    window.location.href = window.location.href;
  } catch (e) {
    window.location.reload();
  }
}

let timeOut = null;

function verifyDeviceAndInitialize() {
  Storages.MainStorage.setItem(StorageKeys.Keys.CHANNELONLY, environment.isChannelOnly());
  return new Promise(async (resolve, rej) => {
    Devices.getDevice();
    const getUID = await DeviceData.getUID();
    const getIP = await DeviceData.getIP();
    console.log(
      '[verifyDeviceAndInitialize] Starting device verification with UID:',
      getUID,
      'IP:',
      getIP
    );
    EventBus.publish('CheckingServer');
    Services.DevicesService.getRegisterDeviceVerification(getUID, getIP)
      .then((res) => {
        console.log('[verifyDeviceAndInitialize] Device verification successful');
        const organization_id = res?.data?.organization_id?.id;
        updateDeviceProxyServer().then((updated) => {
          if (updated) {
            reloadApplication();
            return;
          }

          connectToWebSocket(getUID);
          cacheUpdate(organization_id).then(
            (res) => {
              EventBus.publish('CacheUpdated');
              resolve();
            },
            (error) => {
              if (error && error.status === 403) {
                redirectTo(Route.route.DEVICEIPNOTFOUND);
              }
              rej();
            }
          );
        });
      })
      .catch((err) => {
        console.error('[verifyDeviceAndInitialize] Device verification failed:', err);

        // Remove license key on any error
        if (!environment.isDemoMode()) {
          Storages.MainStorage.removeItem(StorageKeys.Keys.DEVICEREGDATA);
          Storages.MainStorage.removeItem(StorageKeys.Keys.LICENSEAPIKEY);
        }

        // this must always be after above lines
        closeWebSocket();

        let redirected = false;
        const errorStatus = err?.status || 0;

        if (
          errorStatus === 400 ||
          errorStatus === 403 ||
          errorStatus === 500 ||
          errorStatus === 412 ||
          errorStatus === 401 ||
          errorStatus === 409 ||
          errorStatus === 423
        ) {
          if (window.location.pathname !== Route.route.DEVICEIPNOTFOUND) {
            console.log(
              '[verifyDeviceAndInitialize] Redirecting to DEVICEIPNOTFOUND - status:',
              errorStatus
            );
            redirectTo(Route.route.DEVICEIPNOTFOUND);
          }
        } else if (errorStatus === 404 || errorStatus === 0) {
          const isLoggedIn = isLoggedInDevice();
          if (isLoggedIn) {
            console.log('[verifyDeviceAndInitialize] Server down, starting reconnection countdown');
            EventBus.publish('ServerDown');
            reconnectionCountDown();
          } else if (window.location.pathname !== Route.route.NOTFOUND) {
            console.log('[verifyDeviceAndInitialize] Redirecting to NOTFOUND');
            redirectTo(Route.route.NOTFOUND);
            redirected = true;
          }
        }
        environment.isDemoMode() && !redirected ? resolve() : rej();
      });
  });
}
function reconnectionCountDown() {
  timeOut = setTimeout(() => {
    verifyDeviceAndInitialize().then(
      (res) => {
        if (res && res.data) {
          clearTimeout(timeOut);
          EventBus.publish('server_restored');
        }
      },
      (err) => {
        console.error('Error in reconnectionCountDown:', err);
      }
    );
  }, 1000 * 15);
}
function connectToWebSocket(device_id) {
  WebSocketInstance.connect(device_id);
}
function closeWebSocket() {
  WebSocketInstance.disconnect();
}
function cacheUpdate(org_id) {
  return new Promise((resolve, reject) => {
    getData(org_id).then((data_res) => {
      EventBus.publish('refresh', '');
      resolve({ status: true });
    });
  });
}
function getData(org_id) {
  // Run all calls in parallel with customer data fetched after room data
  return getOrganizationGroup(org_id)
    .then(() => getRoomData())
    .then(() =>
      Promise.allSettled([
        getLanguage(),
        getGrpWallpapers(),
        getSliderTimer(),
        getLocation(),
        getLatestNews(),
        getWelcomeNote(),
        getAccreditations(),
        getProfileCategory(),
        getOrgChannel(),
        getOrgGroupChannel(),
        getThemeAndTemplate(),
        getWeatherApi(),
        fetchRegisterationData(),
        getOrganizationIdentity(),
      ])
    )
    .then(() => {
      setLanguageId();
    });
}

function updateGuestRegisteration(payload) {
  let data = undefined;

  if (payload.payload_type === 'CHECKIN') {
    data = { reference_id: payload.reference_id };
  } else if (payload.payload_type !== 'CHECKOUT') {
    return;
  }

  Storages.MainStorage.setItem(StorageKeys.Keys.CUSTOMERDATA, data ?? {});

  return fetchRegisterationData().then(() => {
    if (patientData.hasGuestName() && data) {
      EventBus.publish('showWelcomeScreen');
    }
  });
}

function storePatientProfileMetadata(segments, fields, subFields) {
  Storages.MainStorage.setItem(StorageKeys.Keys.PATIENT_PROFILE_SEGMENTS, segments);
  Storages.MainStorage.setItem(StorageKeys.Keys.PATIENT_PROFILE_FIELDS, fields);
  Storages.MainStorage.setItem(StorageKeys.Keys.PATIENT_PROFILE_SUBFIELDS, subFields);
}

function storeHl7Data(hl7Data) {
  Storages.MainStorage.setItem(StorageKeys.Keys.PATIENT_HL7_DATA, hl7Data ?? {});
}

function clearPatientProfileData() {
  storePatientProfileMetadata([], [], []);
  storeHl7Data({});
}

function updateCustomerData(hl7Data, referenceId) {
  if (!referenceId) {
    Storages.MainStorage.setItem(StorageKeys.Keys.CUSTOMERDATA, {});
    EventBus.publish('refreshCustomerData');
    return;
  }

  storeHl7Data(hl7Data ?? {});
  Storages.MainStorage.setItem(StorageKeys.Keys.CUSTOMERDATA, { reference_id: referenceId });
  EventBus.publish('refreshCustomerData');
}

function fetchPatientProfileMetadata() {
  return Promise.allSettled([
    Services.PatientService.getProfileSegments(),
    Services.PatientService.getProfileFields(),
    Services.PatientService.getProfileSubFields(),
  ]).then(([seg, fld, sub]) => ({
    segments: seg.status === 'fulfilled' ? seg.value?.data ?? [] : [],
    fields:   fld.status === 'fulfilled' ? fld.value?.data ?? [] : [],
    subFields: sub.status === 'fulfilled' ? sub.value?.data ?? [] : [],
  }));
}

function fetchRegisterationData() {
  const customerData = Storages.MainStorage.getItem(StorageKeys.Keys.CUSTOMERDATA) ?? {};

  if (!customerData?.reference_id) {
    clearPatientProfileData();
    updateCustomerData(null, null);
    return Promise.resolve();
  }

  const { reference_id: referenceId } = customerData;

  return fetchPatientProfileMetadata()
    .then(({ segments, fields, subFields }) => {
      storePatientProfileMetadata(segments, fields, subFields);

      const fallbackAttributes = 'pid_patient_name,pid_patient_id';
      const attributes =
        Services.PatientService.buildHl7Attributes(segments, fields) || fallbackAttributes;

      return Services.PatientService.getPatientByReferenceId(referenceId, attributes);
    })
    .then((res) => {
      const hl7Data = res?.data ?? {};
      updateCustomerData(hl7Data, referenceId);
    })
    .catch((err) => {
      console.error('Error in fetchRegisterationData:', err);
      const existingCustomer = Storages.MainStorage.getItem(StorageKeys.Keys.CUSTOMERDATA) ?? {};
      if (!existingCustomer?.reference_id) {
        clearPatientProfileData();
        updateCustomerData(null, null);
        return;
      }
      // Keep last-known HL7 on transient API failures so the guest name does not disappear.
      EventBus.publish('refreshCustomerData');
    });
}


function getRoomData() {
  return Services.DeviceInfoService.getCurrentRoom()
    .then((res) => {
      if (res.data) {
        Storages.MainStorage.setItem(StorageKeys.Keys.ROOMDATA, res.data);
        if (res.data?.group?.id) {
          saveSelectedOrganizationGroup(res.data.group.id);
        }
      } else {
        Storages.MainStorage.setItem(StorageKeys.Keys.ROOMDATA, {});
      }
    })
    .catch(() => {
      Storages.MainStorage.setItem(StorageKeys.Keys.ROOMDATA, {});
    });
}

function clearCache(excludeKeys = []) {
  Storages.MainStorage.clearCacheForRemoteConfig(excludeKeys);
}

async function createGeneralGroup(org_id) {
  try {
    const response = await httpService.post(ApiUrl.ORGANIZATIONGROUP, {
      group_title: 'General',
      organization: org_id,
    });
    return response.data;
  } catch (error) {
    return null;
  }
}

function saveSelectedOrganizationGroup(groupId) {
  Storages.MainStorage.setItem(StorageKeys.Keys.SELECTEDORGANIZATIONGROUP, groupId);
}

function saveGroupsToStorage(groups = []) {
  // Validate that the selected group ID exists in the fetched groups
  const selectedGroupId = Storages.MainStorage.getItem(StorageKeys.Keys.SELECTEDORGANIZATIONGROUP);

  if (selectedGroupId) {
    const groupExists = groups.some((group) => group.id === selectedGroupId);

    // If selected group is not in the fetched groups, remove it
    if (!groupExists) {
      Storages.MainStorage.removeItem(StorageKeys.Keys.SELECTEDORGANIZATIONGROUP);
    }
  }

  Storages.MainStorage.setItem(StorageKeys.Keys.ORGANIZATIONGROUP, groups);
}

function getOrganizationGroup(org_id) {
  return Services.OrganizationGroupService.getOrganizationGroup()
    .then(async (res) => {
      let groups = res?.data || [];

      // Only create General group if there are no groups at all
      if (groups.length === 0) {
        const newGroup = await createGeneralGroup(org_id);
        if (newGroup) {
          groups = [newGroup];
        }
      }

      saveGroupsToStorage(groups);
    })
    .catch(async () => {
      // If initial fetch fails, try to create General group since we have no groups
      const newGroup = await createGeneralGroup(org_id);
      saveGroupsToStorage(newGroup ? [newGroup] : []);
    });
}

function getOrganizationIdentity() {
  return Services.organizationService
    .getOrganizationIdentity()
    .then((res) => {
      const payload = res?.data;
      const normalized =
        payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : null;
      Storages.MainStorage.setItem(StorageKeys.Keys.ORGANIZATION_IDENTITY, normalized);

      const brandColors = normalized?.brand_colors;
      if (brandColors && typeof brandColors === 'object') {
        applyBrandColors(brandColors);
      }
    })
    .catch(() => {
      Storages.MainStorage.setItem(StorageKeys.Keys.ORGANIZATION_IDENTITY, null);
    });
}

function getGrpWallpapers() {
  const params = { group: getDeviceGroupId() };
  return Services.GroupWallpaperService.getGrpWallpapers(params)
    .then((res) => {
      Storages.MainStorage.setItem(StorageKeys.Keys.GRPWALLPAPER, res?.data);
    })
    .catch(() => {
      Storages.MainStorage.setItem(StorageKeys.Keys.GRPWALLPAPER, []);
    });
}

function getSliderTimer() {
  const params = { group_id: getDeviceGroupId() };
  return Services.SliderTimerService.getSliderTimer(params)
    .then((res) => {
      Storages.MainStorage.setItem(StorageKeys.Keys.SLIDERTIMER, res?.data);
    })
    .catch(() => {
      Storages.MainStorage.setItem(StorageKeys.Keys.SLIDERTIMER, []);
    });
}

function getLanguage() {
  return Services.LanguageService.getLanguage()
    .then((res) => {
      Storages.MainStorage.setItem(StorageKeys.Keys.LANGUAGE, res?.data);
    })
    .catch(() => {
      Storages.MainStorage.setItem(StorageKeys.Keys.LANGUAGE, []);
    });
}

function setLanguageId() {
  var language = Storages.MainStorage.getItem(StorageKeys.Keys.LANGUAGE);
  const language_id = Storages.MainStorage.getItem(StorageKeys.Keys.LANGUAGEID);
  if (language_id === null) {
    if (language !== null && language.length) {
      const firstItem = language.filter((arr) => arr.language_code === Code.EN);
      firstItem !== undefined && firstItem.length > 0
        ? Storages.MainStorage.setItem(StorageKeys.Keys.LANGUAGEID, firstItem[0].id)
        : Storages.MainStorage.setItem(StorageKeys.Keys.LANGUAGEID, language[0].id);
    }
  }
}

function getLocation() {
  try {
    return new Promise((resolve, reject) => {
      Services.LocationService.getLocation().then(
        (res) => {
          const locations = res?.data ?? [];
          const currentLocation = locations.find((location) => location.site_location) ?? locations[0];
          Storages.MainStorage.setItem(StorageKeys.Keys.CURRENTLOCATION, currentLocation);
          Storages.MainStorage.setItem(StorageKeys.Keys.ORGANIZATIONLOCATIONS, res?.data);
          resolve(res);
        },
        (err) => {
          localStorage.removeItem(StorageKeys.Keys.CURRENTLOCATION);
          localStorage.removeItem(StorageKeys.Keys.ORGANIZATIONLOCATIONS);
          resolve(err);
        }
      );
    });
  } catch (e) {}
}

function getWeatherApi() {
  return new Promise((resolve, reject) => {
    Services.ThirdPartyService.getThirdPartyService('Weather')
      .then((res) => {
        if (res?.data?.service === 'Weather') {
          const data = res.data;

          // Construct the weather API URL with the city name
          let weatherApi = data.api; //+ '&units=metric';

          // Store the constructed weather API URL in local storage
          Storages.MainStorage.setItem(StorageKeys.Keys.WEATHERAPI, weatherApi);

          // Publish the event to notify that the weather API is ready
          EventBus.publish('getWeather');
        }
        resolve();
      })
      .catch((err) => {
        Storages.MainStorage.setItem(StorageKeys.Keys.WEATHERAPI, '');
        EventBus.publish('getWeather');
        reject(err);
      });
  });
}

function getLatestNews() {
  return Services.LatestNewsService.getLatestNews()
    .then((res) => {
      Storages.MainStorage.setItem(StorageKeys.Keys.LATESTNEWS, res?.data);
    })
    .catch(() => {
      Storages.MainStorage.setItem(StorageKeys.Keys.LATESTNEWS, []);
    });
}

function getWelcomeNote() {
  return Services.WelcomeNoteService.getWelcomeNote()
    .then((res) => {
      Storages.MainStorage.setItem(StorageKeys.Keys.WELCOMENOTE, res?.data);
    })
    .catch(() => {
      Storages.MainStorage.setItem(StorageKeys.Keys.WELCOMENOTE, []);
    });
}

function getAccreditations() {
  return Services.AccreditationService.getAccreditations()
    .then((res) => {
      Storages.MainStorage.setItem(StorageKeys.Keys.ACCREDITATIONS, res?.data);
    })
    .catch(() => {
      Storages.MainStorage.setItem(StorageKeys.Keys.ACCREDITATIONS, []);
    });
}

function getProfileCategory() {
  return Services.ProfileCategoryService.getProfileCategories()
    .then((res) => {
      Storages.MainStorage.setItem(StorageKeys.Keys.PROFILECATEGORY, res?.data);
      if (res?.data?.length) {
        return getProfileByCategory(res.data);
      }
    })
    .catch(() => {
      Storages.MainStorage.setItem(StorageKeys.Keys.PROFILECATEGORY, []);
    });
}

function getProfileByCategory(profile_cat) {
  return Promise.allSettled(
    profile_cat.map((element) =>
      Services.ProfileService.getProfileByCategory(element.id)
        .then((res) => {
          if (res?.data) {
            Storages.MainStorage.setItem(StorageKeys.Keys.PROFILE + element.id, res.data);
          }
        })
        .catch(() => {
          Storages.MainStorage.removeItem(StorageKeys.Keys.PROFILE + element.id);
        })
    )
  );
}

function getOrgChannel() {
  const params = { group_id: getDeviceGroupId() };
  const channelData = createChannelDataService();
  return Services.OrganizationChannelService.getOrganizationChannel(params)
    .then((res) => {
      const orgChannels = res?.data || [];
      Storages.MainStorage.setItem(StorageKeys.Keys.ORGCHANNEL, orgChannels);
      const processedChannels = channelData.processChannelsWithLocales(orgChannels);
      const hasRadioChannel = channelData.hasRadioChannels(processedChannels);
      Storages.MainStorage.setItem(StorageKeys.Keys.HASRADIOCHANNEL, hasRadioChannel);
    })
    .catch(() => {
      // Avoid wiping the last-known channel catalog on transient network/API failures.
      // Keeping existing channels prevents "Live TV is not available" overlays while playback continues.
      const existing = Storages.MainStorage.getItem(StorageKeys.Keys.ORGCHANNEL);
      if (!existing || (Array.isArray(existing) && existing.length === 0)) {
        Storages.MainStorage.setItem(StorageKeys.Keys.ORGCHANNEL, []);
        Storages.MainStorage.setItem(StorageKeys.Keys.HASRADIOCHANNEL, false);
      }
    });
}

function getOrgGroupChannel() {
  return Services.OrganizationGroupChannelService.getOrganizationGroupChannelByGroup()
    .then((res) => {
      Storages.MainStorage.setItem(StorageKeys.Keys.ORGGRPCHANNEL, res?.data);
    })
    .catch(() => {
      Storages.MainStorage.setItem(StorageKeys.Keys.ORGGRPCHANNEL, []);
    });
}

function getThemeAndTemplate() {
  setActiveTemplate();
  setActiveTheme();
}

async function setActiveTemplate() {
  /**
   * if template selected is warwick, and tv is 2K/4k (not 1920x1080)
   * then we would need to add following transform on root element to scale
   * our app otherwise text and components might look tiny
   *
   * const scale = window.innerWidth / 1920;
   * document.documentElement.style.transform = `scale(${scale})`;
   */
  Storages.MainStorage.setItem(StorageKeys.Keys.ACTIVETEMPLATE, Landing.WarwickV1);
}

async function setActiveTheme() {
  Storages.MainStorage.setItem(StorageKeys.Keys.ACTIVETHEME, Theme.CharcoalBlue);
}

/**
 * Helper function to normalize proxy URL by removing trailing slash
 */
function normalizeProxyUrl(url) {
  if (url && url.endsWith('/')) {
    return url.slice(0, -1);
  }
  return url;
}

/**
 * Helper function to get the current device ID
 */
async function getDeviceId() {
  const dev_data = Storages.MainStorage.getItem(StorageKeys.Keys.DEVICEREGDATA);
  return dev_data ? dev_data.device_id : await DeviceData.getUID();
}

/**
 * Helper function to handle proxy URL update process
 */
async function handleProxyUrlUpdate(newUrl, oldUrl) {
  // Set the new proxy URL
  Storages.MainStorage.setItem(StorageKeys.Keys.PROXYURL, newUrl);

  const change = await httpService
    .post(ApiUrl.REGISTERDEVICEVERIFICATION)
    .then(() => false)
    .catch((err) => (err.status === 404 ? false : true));

  Storages.MainStorage.setItem(StorageKeys.Keys.PROXYURL, oldUrl);

  if (!change) {
    return false;
  }

  const device_id = await getDeviceId();

  // Delete notification device registration
  await Promise.all([
    Services.DevicesService.deleteNotificationDevice(device_id),
    Services.DevicesService.markDeviceAliveStatus(false),
  ]).catch(() => undefined);

  Storages.MainStorage.setItem(StorageKeys.Keys.PROXYURL, newUrl);

  return true;
}

/**
 * Updates the device proxy server configuration
 */
async function updateDeviceProxyServer() {
  const device_id = await getDeviceId();
  const currentProxyUrl = Storages.MainStorage.getItem(StorageKeys.Keys.PROXYURL);

  const response = await Services.proxyServerService
    .getDeviceProxyServer(device_id)
    .catch(() => undefined);

  const newProxyUrl = normalizeProxyUrl(
    response?.data?.active ? response?.data?.reference : undefined
  );

  // If URL is undefined or hasn't changed, no update needed
  if (!newProxyUrl || newProxyUrl === currentProxyUrl) {
    return false;
  }

  return await handleProxyUrlUpdate(newProxyUrl, currentProxyUrl);
}

const obj = {
  reconnectionCountDown,
  cacheUpdate,
  clearCache,
  getGrpWallpapers,
  getLocation,
  getWeatherApi,
  getLatestNews,
  getWelcomeNote,
  getAccreditations,
  getSliderTimer,
  getProfileCategory,
  getProfileByCategory,
  getOrgChannel,
  getOrgGroupChannel,
  getLanguage,
  setLanguageId,
  setActiveTemplate,
  setActiveTheme,
  verifyDeviceAndInitialize,
  connectToWebSocket,
  closeWebSocket,
  updateGuestRegisteration,
};

export default obj;
